/** Analytics screen (/w/:ws/analytics). */
export const analytics = {
  title: "Analytics",
  subtitle: "How the assistant is helping your visitors and saving staff time.",
  period: "{{start}} – {{end}}",
  filters: {
    range: "Date range",
    custom: "Custom",
    from: "From",
    to: "To",
    agent: "Agent",
    channel: "Channel",
    invalidRange: "Pick a start date on or before the end date (up to 366 days).",
  },
  export: {
    menu: "Export",
    csv: "CSV (raw daily data)",
    done: "Exported · {{file}}",
  },
  kpi: {
    conversations: "Conversations",
    uniqueUsers: "Unique users",
    messages: "Messages",
    aiResolution: "AI resolution rate",
    handoffRate: "Handoff rate",
    avgResponse: "Avg response time",
    csat: "CSAT",
    hoursSaved: "Est. staff hours saved",
    vsPrevious: "{{value}} vs prev. period",
    pts: "{{value}} pts",
    distinctVisitors: "Distinct visitors",
    perConversation: "{{value}} per conversation",
    p95: "p95 {{value}}",
    ratings: "{{count}} ratings · {{positive}} positive",
    noRatings: "No ratings yet · {{positive}} positive feedback",
    hours: "≈ {{value}} hrs",
    staffValue: "≈ {{value}} in staff time",
    howCalculated: "How is this calculated?",
    assumptionsTitle: "Assumptions",
    assumptionsBody: "{{resolved}} AI-resolved chats × ~{{minutes}} min average staff handling time ≈ {{hours}} hrs. Valued at {{rate}}/hr.",
    assumptionsEdit: "Edit in Settings → Reports.",
    noData: "—",
  },
  trend: {
    title: "Conversations per day",
    subtitle: "{{start}} – {{end}} · current vs previous period",
    current: "This period",
    previous: "Previous",
    emptyTitle: "No conversations in this period",
    emptyBody: "Try a longer date range or another agent or channel.",
  },
  weekly: {
    title: "Resolved vs handed off",
    subtitle: "Per week",
    aiResolved: "AI resolved",
    handedOff: "Handed to staff",
    abandoned: "Abandoned",
  },
  channels: {
    title: "Channel split",
    center: "conversations",
    emptyBody: "Channel shares appear once conversations come in.",
  },
  heatmap: {
    title: "Busiest hours",
    subtitle: "Conversations by day and hour · {{timeZone}}",
    less: "Less",
    more: "More",
    afterHours: "{{pct}} of conversations happen after office hours.",
    days: { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" },
  },
  topics: {
    colTopic: "Topic",
    colConversations: "Conversations",
    colShare: "Share",
    title: "Topic clusters",
    vsPrevious: "vs prev. period",
    seeGaps: "See knowledge gaps",
    uncategorized: "Uncategorized",
    newTopic: "New",
    trendUp: "Up {{value}}",
    trendDown: "Down {{value}}",
    trendFlat: "No change",
    emptyTitle: "No topics yet",
    emptyBody: "Topics are grouped automatically as conversations come in.",
  },
  languages: {
    title: "Language",
    subtitle: "detected per message",
    note: "The assistant replies in the visitor's language.",
    names: { en: "English", fil: "Filipino", tl: "Filipino", taglish: "Taglish", pam: "Kapampangan", ceb: "Cebuano", unknown: "Unknown" },
    emptyBody: "Language mix appears once messages come in.",
  },
  csat: {
    title: "Customer satisfaction",
    collect: "Collect ratings",
    emptyTitle: "No ratings yet",
    emptyBody: "Add an “Ask for a rating” step to a workflow and the ratings appear here.",
    responses: "Responses",
    average: "Average rating",
    positive: "Positive",
    note: "Positive means 4 or 5 stars, or a thumbs up.",
  },
  feedback: {
    title: "Feedback reasons",
    thumbsDown_one: "{{count}} thumbs down",
    thumbsDown_other: "{{count}} thumbs down",
    ofTotal: "{{count}} of {{total}}",
    review: "Review negative feedback",
    reasons: {
      wrong_info: "Incorrect information",
      outdated: "Information was outdated",
      not_what_i_asked: "Didn't answer my question",
      too_long: "Too long / hard to read",
      unspecified: "No reason given",
    },
    emptyTitle: "No negative feedback",
    emptyBody: "Nobody gave a thumbs down in this period.",
  },
  quality: {
    title: "Answer quality",
    runEvaluation: "Run evaluation",
    groundedness: "Groundedness",
    groundednessHint: "Share of answers whose claims are supported by retrieved sources",
    citationRate: "Citation rate",
    citationRateHint: "Share of answers that include at least one citation",
    avgSources: "Avg sources / answer",
    avgSourcesHint: "Average number of distinct sources cited per answer",
    target: "target 1.5–3",
    noAnswer: "No-answer rate",
    noAnswerHint: "Share of questions answered with the fallback reply",
    footnote_one: "Based on {{answers}} graded answers. Hallucination flags this period: {{count}}.",
    footnote_other: "Based on {{answers}} graded answers. Hallucination flags this period: {{count}}.",
  },
  automation: {
    title: "Automation",
    subtitle: "What your workflows did in this period.",
    open: "Open workflows",
    runs: "Workflow runs",
    facingRuns_one: "{{formatted}} customer-facing",
    facingRuns_other: "{{formatted}} customer-facing",
    completion: "Completion",
    completedOf: "{{completed}} of {{runs}} runs reached the end",
    engaged: "Engaged",
    engagedCount_one: "{{formatted}} run tapped or answered",
    engagedCount_other: "{{formatted}} runs tapped or answered",
    resolved: "Resolved in workflows",
    touched_one: "of {{formatted}} conversation a workflow touched",
    touched_other: "of {{formatted}} conversations a workflow touched",
    bookings: "Assisted bookings",
    leads: "Assisted leads",
    assistedHint: "After a workflow ran on the conversation",
    goal: "Goal",
    goalOf: "{{met}} of {{tracked}} runs met their goal",
    failed: "Failed runs",
    failedHint: "A step failed",
    emptyTitle: "No workflow activity in this period",
    emptyBody: "Runs appear here once a live workflow starts.",
    home: {
      title: "Home conversion",
      subtitle: "Widget Home options shown, tapped, and what they led to",
      emptyTitle: "Home wasn't shown",
      emptyBody: "Turn on Widget Home to see how many visitors tap an option.",
      impressions: "Shown",
      taps: "Tapped",
      conversions: "Led to a conversation",
      conversion: "Conversion",
      note: "A conversation opened within 30 minutes of a tap counts as its conversion.",
    },
    top: {
      title: "Top workflows",
      workflow: "Workflow",
      completed: "Completed",
      emptyTitle: "No workflows ran",
      emptyBody: "The workflows that ran most appear here.",
    },
  },
  emptyCardTitle: "No data for this period",
  loadError: "This chart couldn't load.",
};
